import { css } from '@emotion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CalendarDaysIcon } from '$/images/FontAwesomeIcons';
import Dropdown from '../ui/Input/Basics/Dropdown';
import { DropdownProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown';
import { Session, EwebinarSessions, SessionStatus } from '$/GraphQL/schema/schema';
import { DropdownItemProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import { PoweredByEWebinar } from '$/components/Logo';

import { useLocalI18n } from '$/hooks/useLocalI18n';
import { t } from '@lingui/macro';
import Loading from '../Loading';

type ScheduleDropdownProps = Omit<DropdownProps, 'onChange'> & {
  sessions?: EwebinarSessions | null;
  isRegister: boolean;
  primaryColor: string;
  value?: Session;
  onChange?: (session?: Session) => void;
  language: string;
  className?: string;
  hideBranding?: boolean;
  teamId?: string;
  isLoading?: boolean;
  placeholder?: string;
};

const SessionsDropdown: React.FC<ScheduleDropdownProps> = ({
  sessions,
  primaryColor,
  isRegister,
  value,
  onChange,
  className,
  language,
  hideBranding,
  teamId,
  isLoading,
  placeholder,
  ...props
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  const i18n = useLocalI18n(language);

  const loadingSessions = sessions === null || sessions === undefined;
  const ewebinarSessions = React.useMemo(() => sessions?.sessions ?? [], [sessions?.sessions]);
  const sessionOptions: DropdownItemProps[] = React.useMemo(() => {
    const sessionOptions: DropdownItemProps[] = ewebinarSessions.map((s) => {
      const isFull = s.status === SessionStatus.Full;
      if (isFull) {
        return {
          text: (
            <span
              className='line-through'
              css={css`
                cursor: not-allowed;
              `}
            >
              {s.text}
            </span>
          ),
          value: s.value,
          disabled: true,
        };
      }
      return {
        text: s.text,
        value: s.value,
      };
    });

    if (!hideBranding) {
      sessionOptions.push({
        content: (
          <PoweredByEWebinar i18nProvided={i18n} hideBranding={hideBranding} teamId={teamId} />
        ),
        disabled: true,
        style: { display: 'none !important' },
        className: `flex justify-center ${
          hideBranding
            ? 'ewebinar__powered_by_dropdown_item_hidden'
            : 'ewebinar__powered_by_dropdown_item'
        }`,
      });
    }

    return sessionOptions;
  }, [ewebinarSessions, hideBranding, i18n, teamId]);

  const hasAvailableSession = ewebinarSessions.some((s) =>
    [SessionStatus.Available, SessionStatus.AlmostFull].includes(s.status)
  );

  const onDropdownChange = React.useCallback(
    (sessionValue: string): void => {
      const session = ewebinarSessions.find(({ value }) => value === sessionValue);
      if (onChange && session) {
        onChange(session);
      }
    },
    [ewebinarSessions, onChange]
  );

  return (
    <Dropdown
      className={`flex w-full ${className}`}
      css={css`
        border: 2px solid ${primaryColor} !important;
        transition: border 0.5s;
        .menu {
          border: 2px solid ${primaryColor} !important;
          transition: border 0.5s;
          width: calc(100% + 4px) !important;
          left: -1px !important;
          max-height: 22rem !important;
        }
        .menu > div {
          white-space: nowrap;
        }
        & > .text {
          width: 100%;
          text-align: ${langDirClassname(language) === 'rtl' ? 'right' : 'left'};
        }
      `}
      errors={errors}
      placeholder={
        <div
          className='flex justify-between w-full items-center font-light leading-tight'
          css={css`
            margin: -0.15rem 0 0 0;
          `}
        >
          {isLoading ? (
            <Loading className={`w-5 h-5 left-auto top-auto`} />
          ) : (
            <CalendarDaysIcon
              className={`absolute w-5 h-5 ${langDirClassname(language) === 'rtl' && 'hidden'}`}
            />
          )}
          {isLoading && placeholder && (
            <span className='pl-2e text-color-input-field whitespace-nowrap font-light leading-1e'>
              {placeholder}
            </span>
          )}
          <span
            className={`ml-1_5e px-3 ${
              !hasAvailableSession ? 'text-coral-1' : 'text-color-input-field'
            } ${isLoading ? 'hidden' : ''}`}
            css={css`
              line-height: 1rem;
            `}
          >
            {loadingSessions || !i18n
              ? ''
              : sessionOptions.length === 0
                ? t(i18n)`No more sessions`
                : hasAvailableSession
                  ? t(i18n)`Select a session to join`
                  : t(i18n)`No available sessions`}
          </span>
        </div>
      }
      options={sessionOptions}
      value={value?.value}
      onChange={onDropdownChange}
      {...props}
      disabled={isRegister || sessionOptions.length === 0}
    />
  );
};

export default SessionsDropdown;
